import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { ApiService } from '../api-service/api.service';
import {
  SocketService,
  NotificationData,
} from '../socket-service/socket.service';
import { Apis } from '../API';

export interface DropdownNotification {
  id_notification: string;
  titre_fr: string;
  description_fr: string;
  flag_lu: string;
  priorite: string;
  date_creation: string;
  code_type_objet?: string;
  created_by_name?: string;
  type_notification?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationDataService {
  private dropdownNotificationsSubject = new BehaviorSubject<
    DropdownNotification[]
  >([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  public dropdownNotifications$ =
    this.dropdownNotificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private socketService: SocketService,
  ) {
    this.initializeNotificationSync();
  }

  private initializeNotificationSync(): void {
    // Subscribe to socket updates for cross-device sync
    this.socketService.notifications$.subscribe(
      (socketNotifications: NotificationData[]) => {
        // Real-time new notifications (don't replace, just add new ones)
        if (socketNotifications.length > 0) {
          const currentNotifications = this.dropdownNotificationsSubject.value;
          const newNotifications = socketNotifications.filter(
            (socketNotif) =>
              !currentNotifications.find(
                (current) =>
                  current.id_notification === socketNotif.id_notification,
              ),
          );

          if (newNotifications.length > 0) {
            const convertedNotifications: DropdownNotification[] =
              newNotifications.map((socket) => ({
                id_notification: socket.id_notification,
                titre_fr: socket.titre_fr,
                description_fr: socket.description_fr,
                flag_lu: 'N', // New notifications are unread
                priorite: socket.priorite,
                date_creation: socket.date_creation,
                code_type_objet: socket.code_type_objet,
              }));

            const updatedNotifications = [
              ...convertedNotifications,
              ...currentNotifications,
            ].slice(0, 10);
            this.dropdownNotificationsSubject.next(updatedNotifications);
          }
        }
      },
    );

    // Subscribe to socket unread count updates
    this.socketService.unreadCount$.subscribe((count: number) => {
      this.unreadCountSubject.next(count);
    });

    // 🔔 CROSS-DEVICE SYNC: Listen for notifications marked as read on other devices
    this.socketService.connectionStatus$.subscribe((isConnected) => {
      if (isConnected) {
        this.setupCrossDeviceListeners();
      }
    });
  }

  private setupCrossDeviceListeners(): void {
    // Listen for notifications marked as read on other devices
    if (this.socketService.onNotificationReadBroadcast) {
      this.socketService
        .onNotificationReadBroadcast()
        .subscribe((data: any) => {
          if (data && data.notificationId) {
            this.removeNotificationFromDropdown(data.notificationId);
          }
        });
    }

    // Listen for dropdown updates from server (when read on other devices)
    if (this.socketService.onDropdownUpdate) {
      this.socketService.onDropdownUpdate().subscribe((data: any) => {
        if (data && data.notifications) {
          this.dropdownNotificationsSubject.next(data.notifications);
        }
      });
    }
  }

  // Load notifications from API (called on login or component init)
  loadDropdownNotifications(): void {
    this.isLoadingSubject.next(true);

    this.apiService.get(Apis.notifications.dropdown).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const apiNotifications: DropdownNotification[] = response.data;
          this.dropdownNotificationsSubject.next(apiNotifications);
          this.unreadCountSubject.next(response.unread_count || 0);
        }
        this.isLoadingSubject.next(false);
      },
      error: (error) => {
        console.error('❌ Error loading dropdown notifications:', error);
        this.isLoadingSubject.next(false);
      },
    });
  }

  // Open/view notification (automatically marks as read)
  openNotification(notificationId: string): Observable<any> {
    const url = Apis.notifications.open.replace(':id', notificationId);

    return this.apiService.get(url).pipe(
      map((response) => {
        if (response.success) {
          // Remove from dropdown since it's now read
          this.removeNotificationFromDropdown(notificationId);
        }
        return response;
      }),
    );
  }

  // Mark notification as read
  markAsRead(notificationId: string): Observable<any> {
    const url = Apis.notifications.markAsRead.replace(':id', notificationId);

    return this.apiService.post(url).pipe(
      // Changed back to POST for mobile compatibility
      map((response) => {
        if (response.success) {
          // Remove from dropdown since it's now read
          this.removeNotificationFromDropdown(notificationId);

          // Also mark via socket for real-time sync
          this.socketService.markNotificationAsRead(notificationId);
        }
        return response;
      }),
    );
  }

  // Mark all as read
  markAllAsRead(): Observable<any> {
    return this.apiService.post(Apis.notifications.markAllAsRead).pipe(
      // Changed back to POST for mobile compatibility
      map((response) => {
        if (response.success) {
          // Clear all notifications from dropdown
          this.dropdownNotificationsSubject.next([]);
          this.unreadCountSubject.next(0);

          // Also mark via socket for real-time sync
          const currentNotifications = this.dropdownNotificationsSubject.value;
          currentNotifications.forEach((notification) => {
            this.socketService.markNotificationAsRead(
              notification.id_notification,
            );
          });
        }
        return response;
      }),
    );
  }

  // Remove notification from dropdown (used when read)
  private removeNotificationFromDropdown(notificationId: string): void {
    const currentNotifications = this.dropdownNotificationsSubject.value;
    const filteredNotifications = currentNotifications.filter(
      (n) => n.id_notification !== notificationId,
    );

    this.dropdownNotificationsSubject.next(filteredNotifications);

    // Decrease unread count
    const currentCount = this.unreadCountSubject.value;
    if (currentCount > 0) {
      this.unreadCountSubject.next(currentCount - 1);
    }
  }

  // Public method to remove notification (for manual removal)
  removeNotification(notificationId: string): void {
    this.removeNotificationFromDropdown(notificationId);
  }

  // Get current dropdown notifications count
  getCurrentNotificationsCount(): number {
    return this.dropdownNotificationsSubject.value.length;
  }

  // Get current unread count
  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  // Check if notification exists in dropdown
  hasNotification(notificationId: string): boolean {
    return this.dropdownNotificationsSubject.value.some(
      (n) => n.id_notification === notificationId,
    );
  }
}
